import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';
import type { Poster, PosterRepository, UpsertPosterInput } from '@nx/domain';
import { mapPosterDoc, snapshotData } from './firestoreMappers';

function postersCol(db: Firestore, eventId: string) {
  return collection(db, 'events', eventId, 'posters');
}

export class FirebasePosterRepository implements PosterRepository {
  constructor(private readonly db: Firestore) {}

  async getByEvent(eventId: string): Promise<Poster | null> {
    const snap = await getDocs(query(postersCol(this.db, eventId), limit(1)));
    const first = snap.docs[0];
    if (!first) return null;
    return mapPosterDoc(eventId, first.id, snapshotData(first));
  }

  async upsert(eventId: string, input: UpsertPosterInput): Promise<Poster> {
    const existing = await this.getByEvent(eventId);
    if (existing) {
      const patch: Record<string, unknown> = {};
      if (input.templateId !== undefined) patch.templateId = input.templateId;
      if (input.customTitle !== undefined) patch.customTitle = input.customTitle;
      if (input.heroImageUrl !== undefined) patch.heroImageUrl = input.heroImageUrl;
      if (input.logoUrl !== undefined) patch.logoUrl = input.logoUrl;
      if (input.exportedImageUrl !== undefined) patch.exportedImageUrl = input.exportedImageUrl;
      if (input.registrationLink !== undefined) patch.registrationLink = input.registrationLink;
      if (input.qrCodeUrl !== undefined) patch.qrCodeUrl = input.qrCodeUrl;
      await updateDoc(doc(this.db, 'events', eventId, 'posters', existing.id), patch);
      const updated = await this.getByEvent(eventId);
      if (!updated) throw new Error('Poster not found after update');
      return updated;
    }
    const ref = await addDoc(postersCol(this.db, eventId), {
      templateId: input.templateId ?? null,
      customTitle: input.customTitle ?? null,
      heroImageUrl: input.heroImageUrl ?? null,
      logoUrl: input.logoUrl ?? null,
      exportedImageUrl: input.exportedImageUrl ?? null,
      registrationLink: input.registrationLink ?? null,
      qrCodeUrl: input.qrCodeUrl ?? null,
      createdAt: serverTimestamp(),
    });
    const created = await this.getByEvent(eventId);
    if (!created || created.id !== ref.id) {
      return mapPosterDoc(eventId, ref.id, {
        templateId: input.templateId ?? null,
        customTitle: input.customTitle ?? null,
        heroImageUrl: input.heroImageUrl ?? null,
        logoUrl: input.logoUrl ?? null,
        exportedImageUrl: input.exportedImageUrl ?? null,
        registrationLink: input.registrationLink ?? null,
        qrCodeUrl: input.qrCodeUrl ?? null,
        createdAt: null,
      });
    }
    return created;
  }
}
